import type {
  LanguageMatch,
  LanguageRecommendation,
  ManualModule,
  ManualQuery,
  PracticeManual,
  PracticeMatch,
  PracticeManualModule,
  LanguageManualModule
} from "../types";

function tokenize(value: string): string[] {
  const tokens = value
    .toLowerCase()
    .match(/[a-z0-9가-힣]+/g);
  return Array.from(new Set((tokens ?? []).filter((token) => token.length > 1)));
}

function queryText(query: ManualQuery): string {
  return [query.topic, query.keywords, query.situation, query.goal, query.tone].filter(Boolean).join(" ");
}

function scoreHaystack(haystack: string, tokens: string[]): number {
  if (tokens.length === 0) return 1;
  const lower = haystack.toLowerCase();
  return tokens.reduce((score, token) => score + (lower.includes(token) ? 3 : 0), 0);
}

function firstFilled(values: Array<string | undefined>, fallback: string): string {
  return values.find((value) => value && value.trim().length > 0)?.trim() ?? fallback;
}

function localGenerationMeta() {
  return {
    generationMode: "local-fallback" as const,
    aiGenerated: false,
    modelUsed: "local",
    generatedAt: new Date().toISOString()
  };
}

export function searchLanguageFormulas(modules: ManualModule[], query: ManualQuery, limit = 8): LanguageMatch[] {
  const tokens = tokenize(queryText(query));
  const matches: LanguageMatch[] = [];

  for (const module of modules) {
    if (!module.active || module.domain !== "language-pattern") continue;
    for (const formula of module.formulas) {
      const haystack = [
        module.title,
        module.description,
        module.category,
        module.tags.join(" "),
        module.keywords.join(" "),
        formula.group,
        formula.name,
        formula.manualFormula,
        formula.practiceFormula,
        formula.principle,
        formula.triggerSituations.join(" "),
        formula.tags.join(" ")
      ].join(" ");
      const score = scoreHaystack(haystack, tokens);
      if (score > 0) {
        matches.push({
          moduleId: module.id,
          moduleTitle: module.title,
          moduleCategory: module.category,
          formula,
          score,
          reason: tokens.length === 0 ? "기본 추천" : "입력 키워드와 공식의 상황/태그가 겹칩니다."
        });
      }
    }
  }

  matches.sort((a, b) => b.score - a.score || a.moduleTitle.localeCompare(b.moduleTitle));
  if (matches.length > 0) return matches.slice(0, limit);

  const fallbackModule = modules.find(
    (module): module is LanguageManualModule => module.active && module.domain === "language-pattern"
  );
  return fallbackModule
    ? fallbackModule.formulas.slice(0, limit).map((formula) => ({
        moduleId: fallbackModule.id,
        moduleTitle: fallbackModule.title,
        moduleCategory: fallbackModule.category,
        formula,
        score: 1,
        reason: "입력과 직접 일치하는 공식이 없어 기본 공식에서 시작합니다."
      }))
    : [];
}

export function searchPracticeModules(modules: ManualModule[], query: ManualQuery, limit = 8): PracticeMatch[] {
  const tokens = tokenize(queryText(query));
  const matches: PracticeMatch[] = [];

  for (const module of modules) {
    if (!module.active || module.domain !== "psi-qigong") continue;
    for (const practice of module.practices) {
      const haystack = [
        module.title,
        module.description,
        module.category,
        module.tags.join(" "),
        module.keywords.join(" "),
        practice.group,
        practice.name,
        practice.intent,
        practice.recommendedFor.join(" "),
        practice.keywords.join(" "),
        practice.tags.join(" ")
      ].join(" ");
      const score = scoreHaystack(haystack, tokens);
      if (score > 0) {
        matches.push({
          moduleId: module.id,
          moduleTitle: module.title,
          moduleCategory: module.category,
          practice,
          score,
          reason: tokens.length === 0 ? "기본 추천" : "입력 키워드와 훈련 목적/키워드가 겹칩니다."
        });
      }
    }
  }

  matches.sort((a, b) => b.score - a.score || a.moduleTitle.localeCompare(b.moduleTitle));
  if (matches.length > 0) return matches.slice(0, limit);

  const fallbackModule = modules.find(
    (module): module is PracticeManualModule => module.active && module.domain === "psi-qigong"
  );
  return fallbackModule
    ? fallbackModule.practices.slice(0, limit).map((practice) => ({
        moduleId: fallbackModule.id,
        moduleTitle: fallbackModule.title,
        moduleCategory: fallbackModule.category,
        practice,
        score: 1,
        reason: "입력과 직접 일치하는 모듈이 없어 기본 훈련에서 시작합니다."
      }))
    : [];
}

export function buildLanguageFallback(query: ManualQuery, matches: LanguageMatch[]): LanguageRecommendation {
  const topic = firstFilled([query.topic, query.situation], "현재 상황");
  const goal = firstFilled([query.goal], "상대의 반응을 확인하면서 대화를 안전하게 이어가기");
  const tone = firstFilled([query.tone], "차분하고 명료하게");
  const top = matches[0];
  const primaryFormula = top?.formula.practiceFormula || "단정하지 않고 확인 질문으로 바꾼다.";

  return {
    ...localGenerationMeta(),
    summary: topic + "에 대해 " + tone + " 말하되, 목표는 " + goal + "입니다.",
    selectedFormulas: matches.slice(0, 3).map((match) => ({
      moduleTitle: match.moduleTitle,
      formulaName: match.formula.name,
      formulaGroup: match.formula.group,
      manualFormula: match.formula.manualFormula,
      practiceFormula: match.formula.practiceFormula,
      reason: match.reason
    })),
    suggestedLines: [
      primaryFormula,
      "제가 단정하고 싶지는 않습니다. 지금 상황에서 제가 이해한 방향은 " + topic + " 쪽인데, 맞는지 확인해도 될까요?",
      "이 대화는 사실과 선택지를 분리해서 이어가고 싶습니다. 지금 확인할 수 있는 부분부터 말해 주세요."
    ],
    variations: [
      {
        label: "더 단호하게",
        line: "그 표현은 그대로 받아들이기 어렵습니다. " + topic + "에 대해 구체적인 사실 기준으로만 이야기하겠습니다."
      },
      {
        label: "더 부드럽게",
        line: "제가 놓친 부분이 있을 수 있습니다. " + topic + "에서 어떤 지점이 가장 중요했는지 먼저 확인하고 싶습니다."
      },
      {
        label: "대화 종료",
        line: "지금 방식으로는 대화를 이어가기 어렵습니다. 정리한 뒤 나중에 다시 이야기하겠습니다."
      }
    ],
    cautions: [
      "공식은 상대를 속이거나 압박하기 위한 장치가 아니라 확인 질문과 반영 문장으로만 사용합니다.",
      "협박, 공포 유발, 기만, 취약점 악용은 사용하지 않습니다."
    ],
    nextSteps: [
      "상대가 확인한 단어를 다음 문장의 기준으로 삼습니다.",
      "반응이 거칠어지면 종료 조건을 짧게 말하고 대화를 멈춥니다."
    ]
  };
}

export function buildPracticeFallback(
  query: ManualQuery,
  module: PracticeManualModule,
  matches: PracticeMatch[]
): PracticeManual {
  const selected = matches.find((match) => match.moduleId === module.id)?.practice || module.practices[0];
  const topic = firstFilled([query.topic, query.keywords, query.situation], selected?.name || module.title);
  const goal = firstFilled([query.goal], selected?.intent || module.description);

  return {
    ...localGenerationMeta(),
    summary: topic + "에 맞춰 " + module.title + "의 " + (selected?.name || "기본 훈련") + "을 조정합니다.",
    selectedModuleId: module.id,
    selectedModuleTitle: module.title,
    title: (selected?.name || module.title) + " 맞춤 매뉴얼",
    preparation: selected?.setup ?? ["조용한 장소를 정하고 짧은 기록지를 준비합니다."],
    steps: selected?.steps ?? ["호흡을 안정시킵니다.", "주제를 떠올립니다.", "떠오른 감각을 기록합니다."],
    duration: selected?.duration ?? "5-10분",
    adaptations: [
      "오늘 주제: " + topic,
      "훈련 목표: " + goal,
      "결과보다 기록의 일관성을 우선합니다."
    ],
    safety: selected?.safety?.length
      ? selected.safety
      : ["불편감이 생기면 즉시 멈춥니다.", "의학적 치료나 검증된 초능력 주장으로 사용하지 않습니다."],
    logPrompts: selected?.customizationPrompts?.length
      ? selected.customizationPrompts
      : ["훈련 전후 몸 감각은 어떻게 달라졌는가?", "결과 확인 전에 적은 내용은 무엇인가?"],
    visualCue: selected?.visualCue || module.visualCue
  };
}

export function summarizeLanguageMatches(matches: LanguageMatch[]) {
  return matches.slice(0, 6).map((match) => ({
    moduleId: match.moduleId,
    moduleTitle: match.moduleTitle,
    formulaName: match.formula.name,
    formulaGroup: match.formula.group,
    manualFormula: match.formula.manualFormula,
    practiceFormula: match.formula.practiceFormula,
    principle: match.formula.principle,
    cautions: match.formula.cautions,
    reason: match.reason
  }));
}

export function summarizePracticeModule(module: PracticeManualModule, matches: PracticeMatch[]) {
  return {
    id: module.id,
    title: module.title,
    description: module.description,
    category: module.category,
    tags: module.tags,
    keywords: module.keywords,
    practices: matches
      .filter((match) => match.moduleId === module.id)
      .slice(0, 4)
      .map((match) => match.practice)
  };
}
